import React from "react";
import { Group, Stack } from "@mantine/core";
import { Badge, Button } from "@xplatform/design-system";
import { useTranslation } from "react-i18next";
import type { VersionListItem } from "@models/mappingConfig";
import { AdminTable, col, type RowAction } from "@components/AdminTable";

export interface VersionsTableProps {
  items: VersionListItem[];
  loading?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onRowClick?: (row: VersionListItem) => void;
  actions?: (row: VersionListItem) => RowAction[];
  emptyMessage?: string;
}

/** Versions of one plugin, newest first, with the cursor page appended by "Load more". */
export const VersionsTable: React.FC<VersionsTableProps> = ({
  items, loading, hasMore, onLoadMore, onRowClick, actions, emptyMessage,
}) => {
  const { t } = useTranslation();
  const columns = [
    col.twoLine<VersionListItem>("version", t("mappingConfig.columns.version"), (v) => ({
      primary: v.version_id,
      secondary: v.base_version_id ? t("mappingConfig.basedOn", { id: v.base_version_id }) : undefined,
    }), { width: "22%", mono: true }),
    col.status<VersionListItem>("status", t("mappingConfig.columns.status"), (v) => v.status),
    col.date<VersionListItem>("created", t("mappingConfig.columns.createdAt"), (v) => v.created_at),
    col.text<VersionListItem>("createdBy", t("mappingConfig.columns.createdBy"), (v) => v.created_by, { width: "14%" }),
    col.badge<VersionListItem>("source", t("mappingConfig.columns.source"), (v) =>
      v.source_kind ? <Badge variant="light" color="gray">{v.source_kind}</Badge> : "-"),
    col.text<VersionListItem>("comment", t("mappingConfig.columns.comment"), (v) => v.comment, { dimmed: true }),
    col.date<VersionListItem>("activated", t("mappingConfig.columns.activatedAt"), (v) => v.activated_at),
    ...(actions ? [col.actions<VersionListItem>("", actions)] : []),
  ];
  return (
    <Stack gap="sm">
      <AdminTable
        columns={columns}
        data={items}
        getRowKey={(v) => v.version_id}
        onRowClick={onRowClick}
        loading={loading}
        emptyMessage={emptyMessage ?? t("mappingConfig.noVersions")}
      />
      {hasMore && onLoadMore && (
        <Group justify="center">
          <Button variant="light" size="xs" onClick={onLoadMore} loading={loading}>
            {t("mappingConfig.loadMore")}
          </Button>
        </Group>
      )}
    </Stack>
  );
};
